import React from "react";
import { motion } from "framer-motion";
import {
  FiArrowDownCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiMapPin,
  FiShield,
  FiStar,
  FiZap,
} from "react-icons/fi";
import passengerApp from "../../assets/passengerApp.png";

const features = [
  {
    icon: FiShield,
    title: "Secure OTP Login",
    text: "Fast identity checks with a clean, protected sign-in flow.",
  },
  {
    icon: FiMapPin,
    title: "Live Tracking",
    text: "Follow every ride with precise route and arrival visibility.",
  },
  {
    icon: FiCreditCard,
    title: "Wallet Ready",
    text: "Simple payments, wallet access, and transparent trip fares.",
  },
  {
    icon: FiZap,
    title: "Fast Booking",
    text: "Book a ride in seconds with a focused passenger experience.",
  },
];

const floatingCards = [
  {
    icon: FiShield,
    title: "OTP Login",
    className: "left-0 top-10 lg:-left-4",
  },
  {
    icon: FiMapPin,
    title: "Live Tracking",
    className: "right-0 top-20 lg:-right-4",
  },
  {
    icon: FiCreditCard,
    title: "Wallet",
    className: "left-0 top-[45%] lg:-left-8",
  },
  {
    icon: FiClock,
    title: "Booking History",
    className: "right-0 top-[48%] lg:-right-8",
  },
  {
    icon: FiStar,
    title: "Driver Rating",
    className: "left-8 bottom-20 lg:left-4",
  },
  {
    icon: FiZap,
    title: "Fast Booking",
    className: "right-8 bottom-12 lg:right-8",
  },
];

const stats = [
  ["24/7", "Ride support"],
  ["Live", "Trip tracking"],
  ["Secure", "OTP access"],
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function PassengerApp() {
  return (
    <section className="relative isolate overflow-hidden bg-white py-20 text-slate-950 sm:py-24 lg:py-28">
      <div className="absolute inset-0 -z-20 bg-[linear-gradient(to_right,rgba(11,99,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.06)_1px,transparent_1px)] bg-[size:44px_44px]" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_25%_20%,rgba(11,99,246,0.14),transparent_34%),radial-gradient(circle_at_80%_18%,rgba(255,122,0,0.12),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.78),#fff_58%)]" />

      <motion.div
        aria-hidden="true"
        className="absolute left-[-9rem] top-16 h-80 w-80 rounded-full bg-[#0B63F6]/20 blur-3xl"
        animate={{ x: [0, 34, 0], y: [0, -18, 0], scale: [1, 1.08, 1] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute right-[-7rem] top-32 h-72 w-72 rounded-full bg-[#FF7A00]/20 blur-3xl"
        animate={{ x: [0, -28, 0], y: [0, 26, 0], scale: [1, 1.12, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute bottom-[-10rem] left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-[#0B63F6]/10 blur-3xl"
        animate={{ y: [0, -24, 0], scale: [1, 1.06, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-[1fr_0.95fr] lg:gap-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
        >
          <motion.div
            variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full border border-trogoBlue/15 bg-white/70 px-4 py-2 text-sm font-semibold text-trogoBlue shadow-[0_16px_50px_rgba(11,99,246,0.12)] backdrop-blur-xl"
          >
            <FiZap className="text-[#FF7A00]" />
            TROGO Passenger App
          </motion.div>

          <motion.h2
            variants={fadeUp}
            className="mt-6 max-w-3xl text-4xl font-black leading-tight tracking-normal text-slate-900 sm:text-5xl lg:text-6xl"
          >
            The calmest way to{" "}
            <span className="text-trogoBlue">
              move through your city.
            </span>
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-2xl text-lg leading-8 text-slate-600"
          >
            A premium ride experience designed for speed, confidence, and
            clarity, from secure login to live tracking and transparent fares.
          </motion.p>

          <motion.div
            variants={stagger}
            className="mt-9 grid gap-4 sm:grid-cols-2"
          >
            {features.map(({ icon: Icon, title, text }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                whileHover={{ y: -6, scale: 1.02 }}
                className="group rounded-2xl border border-slate-200/80 bg-white/60 p-6 text-center shadow-lg backdrop-blur-xl transition-all duration-300 hover:border-trogoBlue/30 hover:shadow-2xl"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-trogoBlue/10 text-trogoBlue transition group-hover:bg-trogoBlue group-hover:text-white">
                  <Icon className="text-xl" />
                </div>
                <h3 className="mt-5 text-base font-bold text-slate-900">
                  {title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">{text}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-9 max-w-xl space-y-3"
          >
            {stats.map(([value, label]) => (
              <div key={label} className="flex items-center gap-3">
                <FiCheckCircle className="h-5 w-5 shrink-0 text-trogoBlue" />
                <p className="font-semibold text-slate-700">
                  <span className="font-bold text-slate-900">{value}</span> {label}
                </p>
              </div>
            ))}
          </motion.div>

          <motion.a
            variants={fadeUp}
            whileHover={{ y: -3, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            href="https://trogo-bucket.s3.ap-south-1.amazonaws.com/Trogo-Passanger.apk"
            download
            className="mt-9 inline-flex items-center justify-center gap-3 rounded-full bg-trogoBlue px-7 py-4 text-base font-bold text-white shadow-[0_22px_55px_rgba(11,99,246,0.35)] transition hover:bg-[#084fc5]"
          >
            <FiArrowDownCircle className="text-xl" />
            Download Passenger APK
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 48 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative mx-auto flex min-h-[650px] w-full max-w-[660px] items-center justify-center"
        >
          <div className="absolute left-1/2 top-1/2 h-[26rem] w-[26rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0B63F6]/15 blur-3xl" />
          <div className="absolute left-1/2 top-[54%] h-56 w-80 -translate-x-1/2 rounded-full bg-[#FF7A00]/18 blur-3xl" />

          <motion.div
            className="relative z-10"
            animate={{ y: [0, -18, 0], rotate: [-1.5, 1.5, -1.5] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-x-8 bottom-[-2.6rem] h-14 rounded-[100%] bg-slate-950/20 blur-2xl" />
            <div className="relative rounded-[2.4rem] border border-white/80 bg-white/45 p-4 shadow-[0_40px_100px_rgba(15,23,42,0.25)] backdrop-blur-2xl">
              <div className="absolute inset-x-8 top-2 h-px bg-white/90" />
              <img
                src={passengerApp}
                alt="TROGO Passenger App"
                className="relative z-10 w-[255px] rounded-[1.8rem] sm:w-[315px]"
              />
              <div className="pointer-events-none absolute inset-4 rounded-[1.9rem] bg-gradient-to-br from-white/35 via-transparent to-[#0B63F6]/10" />
            </div>
            <div className="mx-auto mt-5 h-16 w-64 rounded-[100%] bg-gradient-to-r from-transparent via-slate-400/25 to-transparent blur-md" />
          </motion.div>

          {floatingCards.map(({ icon: Icon, title, className }, index) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 18, scale: 0.92 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              animate={{ y: [0, index % 2 === 0 ? -10 : 10, 0] }}
              transition={{
                opacity: { duration: 0.45, delay: 0.35 + index * 0.08 },
                y: {
                  duration: 4 + index * 0.25,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              whileHover={{ scale: 1.06, y: -4 }}
              aria-hidden="true"
              className={`absolute z-20 hidden w-44 items-center gap-3 rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm font-bold text-slate-800 shadow-[0_22px_65px_rgba(15,23,42,0.14)] backdrop-blur-2xl md:flex ${className}`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#FF7A00]/12 text-[#FF7A00]">
                <Icon className="text-lg" />
              </span>
              {title}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
